import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Avatar,
  Chip,
  Divider,
} from '@mui/material';
import { Call, CallEnd, Videocam, AccessTime } from '@mui/icons-material';

interface IncomingCallModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAccept: (sessionId: string) => void;
  onDecline: (sessionId: string) => void;
  sessionId?: string;
  callerInfo?: {
    call_type?: 'audio' | 'video';
    customer_id?: string;
    conversation_id?: string;
    timestamp?: string;
  };
}

/**
 * Incoming call modal — uses MUI Dialog to match every other modal/dialog
 * in the agent dashboard (AutomationRuleDialog, AssignmentDialog, etc.).
 */
export const IncomingCallModal: React.FC<IncomingCallModalProps> = ({
  isOpen,
  onClose,
  onAccept,
  onDecline,
  sessionId,
  callerInfo,
}) => {
  const isVideo = callerInfo?.call_type === 'video';
  const [countdown, setCountdown] = useState(30);

  // ── Ringing sound (Web Audio API — no external files needed) ────────────
  useEffect(() => {
    if (!isOpen) {
      setCountdown(30);
      return;
    }

    const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
    const ctx: AudioContext | null = AudioCtx ? new AudioCtx() : null;
    let ringInterval: ReturnType<typeof setInterval> | null = null;

    const playRing = () => {
      if (!ctx) return;
      const gain = ctx.createGain();
      gain.gain.value = 0.07;
      gain.connect(ctx.destination);
      [440, 480].forEach(freq => {
        const osc = ctx.createOscillator();
        osc.type = 'sine';
        osc.frequency.value = freq;
        osc.connect(gain);
        osc.start();
        osc.stop(ctx.currentTime + 0.9);
      });
    };

    playRing();
    ringInterval = setInterval(playRing, 3000);

    // Auto-decline countdown
    const timer = window.setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          window.clearInterval(timer);
          onDecline(sessionId ?? '');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      window.clearInterval(timer);
      if (ringInterval) clearInterval(ringInterval);
      ctx?.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, sessionId]);

  const handleAccept = () => { onAccept(sessionId ?? ''); onClose(); };
  const handleDecline = () => { onDecline(sessionId ?? ''); onClose(); };

  return (
    <Dialog
      open={isOpen}
      onClose={handleDecline}
      maxWidth="xs"
      fullWidth
      sx={{ zIndex: 9999 }}
      PaperProps={{
        sx: {
          borderRadius: 2,
          border: `2px solid ${isVideo ? '#1f93ff' : '#2e7d32'}`,
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {/* Caller avatar */}
          <Avatar
            sx={{
              bgcolor: isVideo ? 'primary.main' : 'success.main',
              width: 44,
              height: 44,
            }}
          >
            {isVideo ? (
              <Videocam fontSize="small" />
            ) : (
              <Call fontSize="small" />
            )}
          </Avatar>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={700} noWrap>
              {isVideo ? 'Incoming Video Call' : 'Incoming Call'}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {callerInfo?.customer_id ?? 'Unknown customer'}
            </Typography>
          </Box>

          {/* Countdown chip */}
          <Chip
            icon={<AccessTime sx={{ fontSize: '14px !important' }} />}
            label={`${countdown}s`}
            size="small"
            color="warning"
            variant="outlined"
          />
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ py: 1.5 }}>
        <Box
          sx={{
            bgcolor: 'hsl(var(--muted) / 0.55)',
            borderRadius: 1,
            px: 1.5,
            py: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
          }}
        >
          <Typography variant="caption" color="text.secondary">
            <strong>Conversation</strong>&nbsp;
            {callerInfo?.conversation_id ?? 'N/A'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            <strong>Time</strong>&nbsp;
            {callerInfo?.timestamp
              ? new Date(callerInfo.timestamp).toLocaleTimeString()
              : new Date().toLocaleTimeString()}
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 2, pb: 2, gap: 1 }}>
        <Button
          variant="outlined"
          color="error"
          startIcon={<CallEnd />}
          onClick={handleDecline}
          fullWidth
        >
          Decline
        </Button>
        <Button
          variant="contained"
          color={isVideo ? 'primary' : 'success'}
          startIcon={isVideo ? <Videocam /> : <Call />}
          onClick={handleAccept}
          fullWidth
        >
          Accept
        </Button>
      </DialogActions>
    </Dialog>
  );
};
